<template>
  <section class="dash-panel">
    <header class="dash-panel-head">
      <div>
        <span class="dash-eyebrow">Équipe</span>
        <h3 class="dash-panel-title">Utilisateurs par rôle</h3>
        <p class="dash-panel-sub">Répartition actuelle des comptes</p>
      </div>
      <span v-if="total" class="dash-chip">{{ total.toLocaleString("fr-FR") }} au total</span>
    </header>

    <AppLoader v-if="isLoader && !roles.length" variant="skeleton" :rows="5" />
    <div v-else-if="!total" class="dash-empty">
      <i class="ph ph-users" aria-hidden="true"></i>
      <p>Aucun utilisateur.</p>
    </div>
    <ul v-else class="role-list" :class="{ 'is-refreshing': isLoader }">
      <li v-for="(role, index) in roles" :key="role.label" class="role-row">
        <span class="role-dot" :style="{ background: role.color }"></span>
        <div class="role-main">
          <div class="role-line">
            <span class="role-name">{{ role.label }}</span>
            <span class="role-value">
              {{ role.value.toLocaleString("fr-FR") }}
              <small>{{ role.share }}%</small>
            </span>
          </div>
          <div class="role-track">
            <span
              class="role-bar"
              :style="{ '--w': role.width + '%', '--d': index * 60 + 'ms', background: role.color }"
            ></span>
          </div>
        </div>
      </li>
    </ul>
  </section>
</template>

<script lang="ts">
import { dashboardGet } from "@/utils/dashboardApi";
import { computed, defineComponent, onMounted, ref } from "vue";

const DEFAULT_LABELS = ["Admin", "Livreur", "Responsable de Zone", "Superviseur", "Suivi"];
const DEFAULT_COLORS = ["#FF5500", "#F59E0B", "#1D9A67", "#3B82F6", "#E44A4A"];

// Same endpoint as before (UsersData); rendered as ranked bars instead of a
// radial chart so every role and its count stay readable.
export default defineComponent({
  name: "UsersDataChart",
  setup() {
    const isLoader = ref(false);
    const series = ref<number[]>([]);
    const labels = ref<string[]>(DEFAULT_LABELS);
    const colors = ref<string[]>(DEFAULT_COLORS);

    const searchData = async () => {
      isLoader.value = true;
      try {
        const data = await dashboardGet("/api/auth/UsersData");
        series.value = (data.series ?? []).map((v: any) => Number(v) || 0);
        // the API sends the role names in `months`
        labels.value = data.months ?? DEFAULT_LABELS;
        colors.value = data.colors ?? DEFAULT_COLORS;
      } catch (error) {
        console.error("Search failed:", error);
      } finally {
        isLoader.value = false;
      }
    };

    const total = computed(() => series.value.reduce((a, b) => a + b, 0));
    const roles = computed(() => {
      const max = Math.max(1, ...series.value);
      return series.value
        .map((value, i) => ({
          label: labels.value[i] || "—",
          color: colors.value[i] || "#A8A29E",
          value,
          width: Math.max(3, Math.round((value / max) * 100)),
          share: total.value ? Math.round((value / total.value) * 100) : 0,
        }))
        .sort((a, b) => b.value - a.value);
    });

    onMounted(() => {
      searchData();
    });

    return { isLoader, searchData, roles, total };
  },
});
</script>

<style scoped>
.role-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin: 0;
  padding: 0;
  list-style: none;
  transition: opacity var(--ui-dur) var(--ui-ease);
}
.role-list.is-refreshing {
  opacity: 0.5;
}
.role-row {
  display: flex;
  align-items: center;
  gap: 14px;
}
.role-dot {
  flex: 0 0 auto;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  box-shadow: 0 0 0 4px var(--ui-surface-sunken);
}
.role-main {
  flex: 1 1 auto;
  min-width: 0;
}
.role-line {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 6px;
}
.role-name {
  overflow: hidden;
  color: var(--ui-text);
  font-size: var(--ui-fs-sm);
  font-weight: var(--ui-fw-heavy);
  text-overflow: ellipsis;
  white-space: nowrap;
}
.role-value {
  color: var(--ui-text);
  font-size: var(--ui-fs-sm);
  font-weight: var(--ui-fw-black);
  font-variant-numeric: tabular-nums;
}
.role-value small {
  margin-left: 4px;
  color: var(--ui-text-muted);
  font-size: var(--ui-fs-micro);
  font-weight: var(--ui-fw-bold);
}
.role-track {
  height: 8px;
  border-radius: var(--ui-radius-pill);
  background: var(--ui-surface-sunken);
  overflow: hidden;
}
.role-bar {
  display: block;
  height: 100%;
  width: var(--w);
  border-radius: inherit;
  transform-origin: left center;
  animation: role-grow 700ms var(--ui-ease-spring) var(--d) both;
}
@keyframes role-grow {
  from { transform: scaleX(0); }
  to { transform: scaleX(1); }
}
@media (prefers-reduced-motion: reduce) {
  .role-bar { animation: none; }
}
</style>
